import { Injectable } from '@angular/core'
import { LoadingService } from './loading.service'
import { MasterKeyService } from './keying.masterkey.service'
import { KeyingDataSetsService } from './keying.data.sets.service'
import { catchError, take, tap } from 'rxjs/operators'
import { ErrorService } from './error.service'
import { DialogService } from './dialog.service'
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component'
import { KeyingBookService } from './keying.book.service'
import { KeyingCylinderService } from './keying.cylinder.service'
import { Observable, throwError } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class MasterKeyUpdateService {
  constructor(
    private loadingService: LoadingService,
    private masterKeyService: MasterKeyService,
    private keyingDataSetsService: KeyingDataSetsService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private keyingBookService: KeyingBookService,
    private keyingCylinderService: KeyingCylinderService
  ) {}

  /* istanbul ignore next */
  deleteMasterKey(structure, keySystem, masterKey, auditTrailRequest: any = undefined) {
    const confirmData = {
      title: 'Delete Key',
      message: `Are you sure you want to delete ${masterKey.keySetSymbol}?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      showCancel: true,
      reasonRequired: false,
    }

    if (keySystem.isPublished) {
      confirmData.message = `The key(s) have been published. Are you sure you want to delete ${masterKey.keySetSymbol}?`
      confirmData.reasonRequired = true
    }

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, confirmData, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        let masterKeyId
        if (masterKey.masterKeyTypeId === 99) {
          masterKeyId = masterKey.id.slice(0, -2)
        } else if (masterKey.masterKeyTypeId === 100) {
          masterKeyId = masterKey.id.slice(0, -3)
        } else {
          masterKeyId = masterKey.id
        }
        this.loadingService.set({ isLoading: true, message: `Removing ${masterKey.keySetSymbol}` })
        this.masterKeyService
          .deleteMasterKeys(structure.id, keySystem.id, masterKeyId, 'master', auditTrailRequest)
          .pipe(take(1))
          .subscribe(
            () => {
              this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
              /* Get Structure Details Everytime Added New KeySetSymbols */
              this.keyingDataSetsService.loadStructureDetails(structure.id)
              this.loadingService.set({ isLoading: false, message: '' })
            },
            (error) => {
              this.loadingService.set({ isLoading: false, message: '' })
              this.errorService.throw(error)
            }
          )
      }
    })
  }

  deleteManyMasterKeys(structure, keySystem, masterKeys) {
    this.loadingService.set({ isLoading: true, message: `Removing Keys` })
    this.masterKeyService
      .deleteManyMasterKeys(structure.id, keySystem.id, masterKeys)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  deleteChildren(structure, keySystem, data, selectedMasterKey?) {
    let confirmData = {
      title: 'Delete Key',
      message: `Are you sure you want to delete ${data.prefix.toUpperCase()}${
        data.from
      }${data.suffix.toUpperCase()}-${data.prefix.toUpperCase()}${data.to}${data.suffix.toUpperCase()}?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      showCancel: true,
      reasonRequired: false,
    }

    if (keySystem.isPublished) {
      confirmData.message = `The key(s) have been published. Are you sure you want to delete ${data.prefix.toUpperCase()}${
        data.from
      }${data.suffix.toUpperCase()}-${data.prefix.toUpperCase()}${data.to}${data.suffix.toUpperCase()}?`
      confirmData.reasonRequired = true
    }

    let dialogRef = this.dialogService.open(ConfirmDialogComponent, confirmData, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.loadingService.set({ isLoading: true, message: `Removing Keys` })
        this.masterKeyService
          .deleteKeysThruNotation(structure.id, keySystem.id, data.masterKey.id, data)
          .pipe(take(1))
          .subscribe(
            () => {
              this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
              /* Get Structure Details Everytime Deleted KeySetSymbols */
              this.keyingDataSetsService.loadStructureDetails(structure.id)
              if (selectedMasterKey) {
                this.keyingBookService.loadFormattedCombinations(structure, keySystem.id, selectedMasterKey)
              }
              this.loadingService.set({ isLoading: false, message: '' })
            },
            (error) => {
              this.loadingService.set({ isLoading: false, message: '' })
              this.errorService.throw(error)
            }
          )
      }
    })
  }

  patchItem(structure, keySystem, patchData, selectedMasterKey?, auditTrailRequest: any = undefined) {
    switch (patchData.type) {
      case 'masterKey':
        this.patchMasterKey(structure, keySystem, patchData, auditTrailRequest)
        break
      case 'note':
        this.patchNote(structure, keySystem, patchData)
        break
      case 'dummyTmk':
        this.toggleDummyTmk(structure, keySystem, selectedMasterKey, patchData, auditTrailRequest)
        break
    }
  }

  toggleDummyTmk(structure, keySystem, selectedMasterKey, patchData, auditTrailRequest: any = undefined) {
    let request = {
      isDummyTMK: patchData.isDummy,
      AuditTrailRequest: auditTrailRequest,
    }

    this.loadingService.set({ isLoading: true, message: `Updating ${patchData.masterKey.keySetSymbol}` })
    this.masterKeyService
      .patchDummyTmk(structure.id, keySystem.id, patchData.masterKey.id, request)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
          this.keyingCylinderService.loadCylinders(structure, keySystem.id, selectedMasterKey.id)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  patchNote(structure, keySystem, patchData) {
    let request = {
      path: patchData.path,
      op: 'replace',
      message: patchData.value,
    }

    this.loadingService.set({
      isLoading: true,
      message: `Updating ${patchData.masterKey.keySetSymbol}`,
      isSilent: true,
    })
    this.masterKeyService
      .postMasterKeysNotes(structure.id, keySystem.id, patchData.masterKey.id, request)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  patchMasterKey(structure, keySystem, patchData, auditTrailRequest) {
    let request = {
      keywayId: patchData.value,
      AuditTrailRequest: auditTrailRequest,
    }

    this.loadingService.set({
      isLoading: true,
      message: `Updating ${patchData.masterKey.keySetSymbol}`,
    })
    this.masterKeyService
      .patchMasterKeys(request, structure.id, keySystem.id, patchData.masterKey.id)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
          this.loadingService.set({ isLoading: false, message: `` })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  addChild(structure, keySystem, data): Observable<any> {
    const loadingMessage = data.action === 'CROSS_KEY_RANGE'
      ? 'Creating Cylinders'
      : `Updating ${data.masterKey.keySetSymbol}`
    this.loadingService.set({ isLoading: true, message: loadingMessage })
    return this.masterKeyService.putMasterKeys(structure.id, keySystem.id, data.masterKey.id, data).pipe(
      take(1),
      tap(() => {
        this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id, true, data.name)
        this.keyingDataSetsService.loadStructureDetails(structure.id, data.action)
        this.loadingService.set({ isLoading: false, message: '' })
      }),
      catchError((error) => {
        if (error) {
          if (error?.error) {
            if (error?.error?.message) this.errorService.showError(error.error.message)
            else if (error.error.errors) this.errorService.showError('Invalid KeySetSymbol')
            else this.errorService.showError(error.error)
          } else this.errorService.showError(error)
        }

        this.keyingDataSetsService.loadKeySystemDataSets(structure.id, keySystem.id)
        this.keyingDataSetsService.loadStructureDetails(structure.id)
        this.loadingService.set({ isLoading: false, message: '' })

        // Rethrow error so component can handle it if needed
        return throwError(() => error)
      })
    )
  }
}
